import { createContext, useReducer, useEffect } from 'react';

import { AtlBrowserStorageSession } from '@atlassian/browser-storage-controls';

import {
  getCookieValue,
  type CookieValue,
  getPipelinesEnvCookieValue,
  type PipelineEnvOption,
} from './dev-asset-cookie-utils';
import { useExperimentsOverrides } from './use-experiments-overrides';
import { useFeatureGateOverrides } from './use-feature-gate-overrides';

const SESSION_KEY_HIDE_BADGE = 'bbc-devtools-hide-badge';

type FeatureGates = Record<string, boolean>;
type Experiments = Record<string, Record<string, unknown>>;
type ContextType = {
  isEnabled: boolean;
  isDialogVisible: boolean;
  isBadgeVisible: boolean;
  frontbucketVersion: CookieValue;
  pipelinesVersion: PipelineEnvOption;
  hasAssetOverrides: boolean;
  hasFeatureOverrides: boolean;
  hasExperimentOverrides: boolean;
  featureGates: FeatureGates;
  experiments: Experiments;
};

type DevToolsAction =
  | { type: 'TOGGLE_DIALOG' }
  | { type: 'CLOSE_DIALOG' }
  | { type: 'TOGGLE_BADGE_VISIBLE' }
  | { type: 'UPDATE_FEATURES'; payload: { featureGates: FeatureGates } }
  | { type: 'UPDATE_EXPERIMENTS'; payload: { experiments: Experiments } };

const emptyState = (isEnabled: boolean) => ({
  isEnabled,
  isBadgeVisible: false,
  isDialogVisible: false,
  frontbucketVersion: 'server' as CookieValue,
  pipelinesVersion: '' as PipelineEnvOption,
  hasAssetOverrides: false,
  hasFeatureOverrides: false,
  hasExperimentOverrides: false,
  featureGates: {},
  experiments: {},
});

export const DevToolsContext = createContext<ContextType>(emptyState(false));
export const DevToolsDispatchContext = createContext<
  React.Dispatch<DevToolsAction>
>(
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  () => {}
);

function getInitialState(initialValue: ContextType): ContextType {
  if (initialValue.isEnabled === false) {
    return initialValue;
  }

  const frontbucketVersion = getCookieValue();
  const pipelinesVersion = getPipelinesEnvCookieValue();
  const isBadgeVisible =
    AtlBrowserStorageSession.getItem(SESSION_KEY_HIDE_BADGE) !== 'true';

  const hasAssetOverrides =
    frontbucketVersion !== 'server' || pipelinesVersion !== '';

  return {
    isEnabled: true,
    isBadgeVisible,
    isDialogVisible: false,
    frontbucketVersion,
    pipelinesVersion,
    hasAssetOverrides,
    hasFeatureOverrides: false,
    hasExperimentOverrides: false,
    featureGates: {},
    experiments: {},
  };
}

function reducer(state: ContextType, action: DevToolsAction): ContextType {
  switch (action.type) {
    case 'TOGGLE_DIALOG':
      return {
        ...state,
        isDialogVisible: !state.isDialogVisible,
      };
    case 'CLOSE_DIALOG':
      return {
        ...state,
        isDialogVisible: false,
      };
    case 'TOGGLE_BADGE_VISIBLE':
      return {
        ...state,
        isBadgeVisible: !state.isBadgeVisible,
      };
    case 'UPDATE_FEATURES':
      return {
        ...state,
        featureGates: action.payload.featureGates,
        hasFeatureOverrides:
          Object.keys(action.payload.featureGates).length > 0,
      };
    case 'UPDATE_EXPERIMENTS':
      return {
        ...state,
        experiments: action.payload.experiments,
        hasExperimentOverrides:
          Object.keys(action.payload.experiments).length > 0,
      };
    default:
      return state;
  }
}

type Props = {
  isEnabled: boolean;
  children: React.ReactNode;
};

export const DevToolsProvider = ({ children, isEnabled }: Props) => {
  const [state, dispatch] = useReducer(
    reducer,
    emptyState(isEnabled),
    getInitialState
  );
  const featureGates = useFeatureGateOverrides();
  const experiments = useExperimentsOverrides();

  useEffect(() => {
    dispatch({ type: 'UPDATE_FEATURES', payload: { featureGates } });
  }, [featureGates]);

  useEffect(() => {
    dispatch({ type: 'UPDATE_EXPERIMENTS', payload: { experiments } });
  }, [experiments]);

  useEffect(() => {
    AtlBrowserStorageSession.setStrictlyNecessaryItem(
      SESSION_KEY_HIDE_BADGE,
      state.isBadgeVisible ? 'false' : 'true'
    );
  }, [state.isBadgeVisible]);

  return (
    <DevToolsContext.Provider value={state}>
      <DevToolsDispatchContext.Provider value={dispatch}>
        {children}
      </DevToolsDispatchContext.Provider>
    </DevToolsContext.Provider>
  );
};
